<template>
  <div>
    <div v-if="study" class="view-row">
      <div class="summary-box">
        <div class="icon-holder">
          <i class="icon-calendar"></i>
        </div>
        <span class="summary-baseline">{{ $t('studyDate') }}</span>
        <p class="caption">{{ date }}</p>
      </div>
      <div class="summary-box">
        <div class="icon-holder">
          <i class="icon-clock"></i>
        </div>
        <span class="summary-baseline">{{ $t('studyTime') }}</span>
        <p class="caption">{{ travelTimeType }} {{ time }}</p>
      </div>
      <div class="summary-box">
        <div class="icon-holder">
          <i class="icon-directions"></i>
        </div>
        <span class="summary-baseline">{{ $t('routeType') }}</span>
        <p class="caption">{{ travelType }}</p>
      </div>
      <div class="summary-box larger">
        <div class="icon-holder">
          <i class="icon-bus"></i>
        </div>
        <span class="summary-baseline">{{ $t('calculationBasis') }}</span>
        <p v-for="(text, idx) in base" :key="idx" class="caption">{{ text }}</p>
      </div>
      <div class="summary-box larger">
        <div class="icon-holder">
          <i class="icon-people"></i>
        </div>
        <table v-if="study">
          <tbody>
            <tr>
              <td>{{ study.collaborators_total }}</td>
              <td>{{ $t('parameters.totalCollaborators', study.collaborators_total) }}</td>
            </tr>
            <tr>
              <td>{{ study.collaborators_recognized }}</td>
              <td>{{ $t('parameters.accountedCollaborators', study.collaborators_recognized) }}</td>
            </tr>
            <tr>
              <td>{{ study.unrecognized_addresses }}</td>
              <td>{{ $t('parameters.unrecognizedAddress', study.unrecognized_addresses) }}</td>
            </tr>
            <tr>
              <td>{{ study.off_limit_addresses }}</td>
              <td>{{ $t('parameters.offlimitAddress', study.off_limit_addresses) }}</td>
            </tr>
            <tr>
              <td>{{ study.excluded_addresses }}</td>
              <td>{{ $t('parameters.excludedAddress', study.excluded_addresses) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-if="desktop" class="icon-container">
      <div class="more-info" title="Plus d'info" @click="moreInfo">i</div>
    </div>
  </div>
</template>

<script>
export default {
  props: ['study', 'desktop'],

  computed: {
    date() {
      if (typeof this.study.date === 'number') {
        // legacy
        return this.$d(new Date(this.study.date * 1000), 'date');
      }

      return this.$d(new Date(this.study.date), 'date');
    },

    travelTimeType() {
      return this.study.travel_time_type === 1 ? `${this.$t('arrivalAt')} ` : `${this.$t('departureAt')} `;
    },

    time() {
      let dateTime = new Date(this.study.date);
      if (typeof this.study.date === 'number') {
        // legacy
        dateTime = new Date(this.study.date * 1000);
      }

      return `${dateTime.getHours()}h${dateTime.getMinutes() > 0 ? dateTime.getMinutes() : ''}`;
    },

    travelType() {
      return this.study.travel_type === 1 ? this.$t('routeOneWay') : this.$t('routeReturn');
    },

    base() {
      const base = [];
      if (this.study.personalized_transport_only) {
        base.push(this.$t('transportMode.custom'));
        return base;
      }

      if (this.study.individual_transport_only === false) {
        base.push(this.$t('transportMode.public') + (this.study.futur_transport_type !== 0 ? ` ${this.$t('transportMode.actuals')}` : ''));
        if (this.study.futur_transport_type !== 0) {
          const futureTransportTypeMap = {
            1: 'transportMode.reach2025',
            2: 'transportMode.reach2030',
            3: 'transportMode.reach2027',
            4: 'transportMode.reach2031',
          };

          base.push(this.$t('transportMode.public') + ' ' + this.$t(futureTransportTypeMap[this.study.futur_transport_type]));
        }
      }
      base.push(this.$t('transportMode.private'));
      if (this.study.individual_transport_only === false) {
        base.push(this.$t('transportMode.bicycle'));

        if (this.study.withWalkingTimes) {
          base.push(this.$t('transportMode.walking'));
        }
      }
      return base;
    },

    countedCollaborators() {
      const { study } = this;
      return study.collaborators.length - (study.unrecognized_addresses + study.off_limit_addresses + study.excluded_addresses);
    },
  },

  methods: {
    moreInfo() {
      this.$store.dispatch('updateCurrentModal', {
        component: 'TransportDetail',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';
.view-row {
  display: flex;
}
.summary-box {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  padding: 0 16px;
  &.larger {
    flex: 1.5;
  }
  & + .summary-box {
    border-left: 1px solid #e3e3e3;
  }
  .icon-holder {
    display: flex;
    justify-content: center;
    align-items: center;
    i {
      color: #dbd6c7;
      font-size: 2em;
    }
  }
  .summary-baseline {
    color: #77797f;
    text-align: center;
    margin-bottom: 12px;
    font-size: 14px;
  }
  .caption {
    font-weight: 600;
    margin: 0;
    text-align: center;
    &::first-letter {
      text-transform: uppercase;
    }
  }
  table {
    td {
      &:first-child {
        font-weight: 600;
        text-align: right;
      }
    }
  }
}
.thumb {
  margin-bottom: 1.2em;
  border-bottom: 1px solid #e3e3e3;
  padding-bottom: 1.2em;
  .summary-box {
    &:first-child {
      padding-left: 0;
    }
    &:last-child {
      padding-right: 0;
      flex: 2;
    }
    .icon-holder {
      i {
        color: #3c3c3b;
      }
    }
    .caption {
      font-size: 14px;
      color: #3c3c3b;
    }
    table {
      td {
        font-size: 14px;
        color: #3c3c3b;
      }
    }
  }
}
.icon-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  .more-info {
    $radius: 16px;
    width: $radius;
    height: $radius;
    border-radius: $radius;
    line-height: $radius;
    text-align: center;
    font-weight: bold;
    font-size: calc($radius * 2 / 3);
    color: colors.$main-color;
    border: solid 2px colors.$main-color;
    cursor: pointer;
    margin: 8px 0 -8px;
    transition: 0.2s ease-out;

    &:hover {
      background: colors.$main-color;
      color: colors.$white-color;
    }

    &:active {
      background: colors.$main-color;
      color: colors.$white-color;
      transform: scale(0.9);
    }
  }
}
</style>
